import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Button, GridLegacy as Grid, LinearProgress } from '@mui/material';

import { useConfirm } from 'material-ui-confirm';
import { Page, Row } from '../../util/Page';

import getBuildingStaging from '../../../redux/selectors/staging';
import {
  setPage,
  demoteStaging,
  updateStagingPoints,
  updateStagingEquipment,
  createStagingEquipment,
  stagingEquipmentDelete,
} from '../../../redux/actions/staging';
import StagingFilterBuilder, { FilterDefinition } from '../elements/StagingFilterBuilder';
import PointTypeFilterPopover from '../elements/filter/PointTypeFilterPopover';
import TextFilterPopover from '../elements/filter/TextFilterPopover';
import BooleanFilterOption from '../elements/filter/BooleanFilterOption';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import EquipmentLoader from '../../loaders/EquipmentLoader';
import LocationLoader from '../../loaders/LocationLoader';
import { DeploymentsLoader } from '../../loaders/deployments';
import { PointSelectorDialog } from '../elements/dialogs/PointSelectorDialog';
import { FilterProvider } from '../contexts/FilterContext';
import {
  EquipmentTypeFilter,
  ContainsTextFilter,
  PointTypeManyFilter,
  BooleanFilter,
} from '../../../redux/constants/filters';
import EquipmentTypeFilterPopover from '../elements/filter/EquipmentTypeFilterPopover';
import EquipmentRelationList from '../elements/EquipmentRelationList';
import EquipmentPointsCard from '../elements/EquipmentPointsCard';
import EditEquipmentDialog from '../elements/dialogs/EditEquipmentDialog';

const OverviewView = ({
  loaded,
  points,
  equipment,
  pointTypes,
  buildingId,
  promotedPointsById,
  locationsByName,
  promotedEquipmentById,
  deployments,

  updateStagingPointsUncaught,
  demoteStagingUncaught,
  updateStagingEquipmentUncaught,
  createStagingEquipmentUncaught,
  deleteStagingEquipmentUncaught,
  resetPage,
}) => {
  const filterDefs = useMemo(
    () => [
      new FilterDefinition(
        EquipmentTypeFilterPopover,
        'equip_type',
        'Equip Type',
        {},
        (type) =>
          new EquipmentTypeFilter({
            key: 'equip_type',
            getter: (e) => e.equipment_type_id,
            type,
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'equip_name',
        'Equip Name',
        {},
        (v) => new ContainsTextFilter({ key: 'equip_name', getter: (e) => e.name, fragment: v })
      ),
      new FilterDefinition(
        PointTypeFilterPopover,
        'point_type',
        'Point Type',
        {},
        (type) =>
          new PointTypeManyFilter({
            key: 'point_type',
            getter: (e) => Array.from(e.points?.map((p) => p.point_type_id) ?? []),
            type,
          }),
        (type) => type.name
      ),
      new FilterDefinition(
        TextFilterPopover,
        'device_id',
        'Device ID',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'device_id',
            getter: (e) => (e.devices ?? []).map((d) => d.device_id).join(' '),
            fragment: v,
          })
      ),
    ],
    []
  );
  const allNames = useMemo(
    () =>
      Object.keys(locationsByName).concat(Object.values(promotedEquipmentById).map((e) => e.name)),
    [locationsByName, promotedEquipmentById]
  );

  const confirm = useConfirm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pointSelectorOpen, setPointSelectorOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState({});

  const onAddPoint = (equip) => {
    if (pointSelectorOpen) return;

    setEditingEquipment(equip);
    setPointSelectorOpen(true);
  };

  const onDeleteEquipment = (equip) => {
    /* equip, point */
    confirm({
      description: `Are you sure you want to delete equipment ${equip.equip_id}? This action is permanent!`,
    }).then(({ confirmed }) =>
      confirmed ? deleteStagingEquipmentUncaught([equip.equip_id]) : null
    );
  };

  const handleAddEquipment = () => {
    setEditingEquipment({});
    setEquipmentOpen(true);
  };

  const handleEditEquipment = (equip) => {
    setEditingEquipment(equip);
    setEquipmentOpen(true);
  };

  const handleEquipmentUpdate = (edittedEquipment) => {
    if (edittedEquipment.name) {
      // rename
      updateStagingEquipmentUncaught([
        {
          name: edittedEquipment.name,
          new_name: edittedEquipment.new_name,
        },
      ]);
    } else {
      // create
      createStagingEquipmentUncaught(edittedEquipment.new_name);
    }
  };

  // Need this to generate initial values, but plumbing for the rest is tricky
  const hideOrphans = useMemo(
    () =>
      new FilterDefinition(
        BooleanFilterOption,
        'hide_orphans',
        'Hide points with no equip',
        {},
        () =>
          new BooleanFilter({
            key: 'hide_orphans',
            getter: (p) => p.equip,
            evaluation: (equip) => (equip?.length ?? 0) > 0,
          }),
        (val) => val
      ),
    []
  );
  if (!loaded) {
    return (
      <Page>
        <Row>
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <EquipmentLoader key={buildingId} buildingId={buildingId} />
          <LocationLoader key={`ll-${buildingId}`} buildingId={buildingId} />
          <DeploymentsLoader key="deployments" />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  return (
    <FilterProvider onFilterChange={() => setCurrentPage(1)}>
      <Grid item xs={12}>
        <StagingFilterBuilder filterDefs={filterDefs} />
      </Grid>
      <Grid container item direction="row" xs={12} sx={{ justifyContent: 'flex-end' }}>
        <Button color="success" disabled={!loaded} variant="contained" onClick={handleAddEquipment}>
          Add Equipment
        </Button>
      </Grid>
      <Grid item xs={12}>
        <EquipmentRelationList
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          equipment={equipment}
          loading={!loaded}
          renderChild={(e) => (
            <EquipmentPointsCard
              key={`${e.equip_id}-card`}
              buildingId={buildingId}
              equipment={e}
              onUpdateEquipment={handleEditEquipment}
              onDeleteEquipment={onDeleteEquipment}
              onAddPoint={onAddPoint}
            />
          )}
        />
      </Grid>
      <Grid container item direction="row" xs={12} sx={{ justifyContent: 'flex-end' }}>
        <Button color="success" disabled={!loaded} variant="contained" onClick={handleAddEquipment}>
          Add Equipment
        </Button>
      </Grid>
      <FilterProvider initialValue={[hideOrphans.filterBuilder(true)]} onFilterChange={resetPage}>
        <EditEquipmentDialog
          open={equipmentOpen}
          buildingId={buildingId}
          onClose={() => setEquipmentOpen(false)}
          equip={editingEquipment}
          allNames={allNames}
          onSubmit={handleEquipmentUpdate}
        />
        <PointSelectorDialog
          open={pointSelectorOpen}
          onClose={() => setPointSelectorOpen(false)}
          points={points}
          pointTypes={pointTypes}
          buildingId={buildingId}
          equipment={editingEquipment}
          promotedEquipmentById={promotedEquipmentById}
          deployments={deployments}
          hideOrphans={hideOrphans}
          title={`Equipment ${editingEquipment?.name}: Edit Points`}
          onDemote={(removedTopicSet) => {
            const equipmentId = editingEquipment.promoted_equipment_id;

            const promotedEquipmentPoints = Array.from(removedTopicSet)
              .map((topic) => points.get(topic).promoted_point_id)
              .filter(Boolean)
              .filter((id) => promotedPointsById[id].equipIds.includes(equipmentId));

            const orphanedPoints = promotedEquipmentPoints.filter(
              (id) =>
                promotedPointsById[id].equipIds?.length === 1 &&
                promotedPointsById[id].equipIds[0] === equipmentId
            );

            return demoteStagingUncaught({
              equipment_ids: [],
              point_ids: orphanedPoints,
              point_equipment_relationships: promotedEquipmentPoints.map((point) => ({
                equipment_id: equipmentId,
                point_id: point,
              })),
            });
          }}
          onSubmit={(selectedPoints) => {
            const equipPoints = Array.from(editingEquipment.points ?? []);
            const equipTopics = equipPoints.map((p) => p.topic);

            const newPoints = selectedPoints.filter((p) => !equipTopics.includes(p.topic));

            const updates = newPoints.map((p) => ({
              topic: p.topic,
              equip_names: [
                ...p.equip_ids.map((eid) => equipment.get(eid).name),
                editingEquipment.name,
              ],
            }));

            const deletes = equipPoints
              .filter((p) => !selectedPoints.includes(p))
              .map((point) => {
                const equipIds = [...point.equip_ids];
                const index = equipIds.indexOf(editingEquipment.equip_id);
                if (index > -1) {
                  equipIds.splice(index, 1);
                }
                return {
                  topic: point.topic,
                  equip_names: equipIds.map((eid) => equipment.get(eid).name),
                };
              });
            return updateStagingPointsUncaught(updates.concat(deletes));
          }}
        />
      </FilterProvider>
    </FilterProvider>
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  ...getBuildingStaging(state, buildingId),
  loaded: state.entities.staging.loaded[buildingId] ?? false,
  pointTypes: state.entities.pointTypes.byId,
  promotedPointsById: state.entities.points.byId ?? {},
  promotedEquipmentById: state.entities.equipment.byId ?? {},
  deployments:
    Object.fromEntries(state.entities.deployments.deployments?.map((d) => [d.site, d.name])) ?? {},

  locationsByName: state.entities.buildings.locationsByBuildingIdName[buildingId] ?? {},
  buildingId,
});

const mapDispatchToProps = (dispatch, { buildingId }) => ({
  resetPage: () => dispatch(setPage(0)),
  updateStagingPointsUncaught: (data) => dispatch(updateStagingPoints(buildingId, data)),
  demoteStagingUncaught: (data) => dispatch(demoteStaging(buildingId, data)),
  createStagingEquipmentUncaught: (equipIds, data) =>
    dispatch(createStagingEquipment(buildingId, equipIds, data)),
  updateStagingEquipmentUncaught: (equipIds, data) =>
    dispatch(updateStagingEquipment(buildingId, equipIds, data)),
  deleteStagingEquipmentUncaught: (equipIds) =>
    dispatch(stagingEquipmentDelete(buildingId, equipIds)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OverviewView);
